import { Icon, IconName, IconSize } from '@ardoq/icons';
import { Header4, Link, Text } from '@ardoq/typography';
import styled from 'styled-components';
import { SecondaryButton, GhostButton } from '@ardoq/button';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { space } from '@ardoq/design-tokens';
import { Badge } from '../Badge';
import { BadgeProperties } from '../Badge/types';

/**
 * Style specs are followed to facilitate the default layout in the current use case, while
 * not inhibiting the flexibility of the flex layout as much as it makes sense now.
 */

const NotificationContainer = styled(FlexBox)`
  pointer-events: auto;
  max-width: 859px;
`;

const ActionButton = styled(SecondaryButton)`
  flex-shrink: 0;
`;

const DescriptionLink = styled(Link)`
  margin-left: ${space.small};
`;

export type IconConfig = {
  iconName: IconName;
  iconSize: IconSize;
  color: string;
};

export type InteractiveNotificationConfig = {
  title?: string;
  description: React.ReactNode;
  badgeConfig?: BadgeProperties;
  iconConfig?: IconConfig;
  actionButtonText?: string;
  externalLink?: { text: string; url: string };
};
type InteractiveNotificationProps = {
  config: InteractiveNotificationConfig;
  onClose: VoidFunction;
  onActionButtonClick?: VoidFunction;
  onExternalLinkClick?: VoidFunction;
  notificationBodyStyle?: React.CSSProperties;
};
export const InteractiveNotification = ({
  config,
  onClose,
  onActionButtonClick,
  onExternalLinkClick,
  notificationBodyStyle,
}: InteractiveNotificationProps) => {
  const {
    title,
    description,
    badgeConfig,
    actionButtonText,
    iconConfig,
    externalLink,
  } = config;

  return (
    <NotificationContainer
      gap="small"
      align="center"
      paddingY="medium"
      paddingRight="medium"
      paddingLeft="large"
      borderColor="borderSubtle"
      borderWidth={1}
      borderRadius="r8"
      backgroundColor="surfaceDefault"
      style={notificationBodyStyle}
    >
      {iconConfig ? (
        <Icon
          iconName={iconConfig.iconName}
          iconSize={iconConfig?.iconSize}
          color={iconConfig.color}
        />
      ) : null}
      <Stack flex={1}>
        <FlexBox gap="small" align="center" flex={1}>
          {badgeConfig && <Badge {...badgeConfig} />}
          {title ? <Header4>{title}</Header4> : null}
        </FlexBox>
        <Box marginTop="small">
          <Text variant="text2">
            {description}
            {externalLink ? (
              <DescriptionLink
                typography="text2"
                href={externalLink.url}
                target="_blank"
                rel="noreferrer"
                keepDefaultNavigation
                // onAuxClick handles middle-click (button === 1) to track
                // link clicks that open in a new tab, since onClick is not
                // fired for middle-clicks.
                onAuxClick={e => {
                  if (e.button === 1) {
                    onExternalLinkClick?.();
                  }
                }}
                onClick={onExternalLinkClick}
              >
                {externalLink.text}
              </DescriptionLink>
            ) : null}
          </Text>
        </Box>
      </Stack>
      {onActionButtonClick && actionButtonText ? (
        <ActionButton onClick={onActionButtonClick}>
          {actionButtonText}
        </ActionButton>
      ) : null}
      <GhostButton onClick={onClose}>
        <Icon iconName={IconName.CLOSE} />
      </GhostButton>
    </NotificationContainer>
  );
};
